class KlaviyoEmailPreferences extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.emailField = this.querySelector('[data-email-field]');
  }

  connectedCallback() {
    this.onFormSubmit = this.onFormSubmit.bind(this);
    if (!this.form) return;

    this.form.addEventListener('submit', this.onFormSubmit);

    // A Klaviyo email link carries _kx, which identifies the profile without exposing the address
    const exchangeId = new URLSearchParams(window.location.search).get('_kx');
    if (exchangeId) {
      this.exchangeId = exchangeId;
      if (this.emailField) this.emailField.classList.add('hidden');
      return;
    }

    // Without _kx the visitor has to tell us who they are
    const input = this.emailField?.querySelector('input');
    if (input) input.required = true;
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.onFormSubmit);
  }

  #removeMessages() {
    this.querySelector('[data-message-success]').classList.add('hidden');
    this.querySelector('[data-message-error]').classList.add('hidden');
  }

  #setLoadingState(isLoading) {
    this.submitButton.classList.toggle('loading', isLoading);
    const spinner = this.submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  #profileAttributes(formData) {
    if (this.exchangeId) return { _kx: this.exchangeId };
    // Klaviyo keys profiles on the exact string: a trailing space or a capital
    // letter creates a second profile for the same person.
    return { email: String(formData.get('email') || '').trim().toLowerCase() };
  }

  async onFormSubmit(event) {
    event.preventDefault();
    this.#removeMessages();
    this.#setLoadingState(true);

    const formData = new FormData(event.target);
    const companyId = this.dataset.companyId;
    const metricName = this.dataset.metricName;

    try {
      // The event is the only write. A Klaviyo flow reads it and calls the subscribe or
      // unsubscribe endpoint, which needs a private key we cannot ship to the browser.
      const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Revision: '2024-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: metricName },
                },
              },
              properties: {
                email_tracking_consent: formData.get('email_tracking_consent') === 'granted',
                source: 'theme_preference_page',
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: this.#profileAttributes(formData),
                },
              },
            },
          },
        }),
      });

      if (response.status !== 200 && response.status !== 202) {
        throw new Error(`Klaviyo returned ${response.status}`);
      }

      this.form.classList.add('hidden');
      this.querySelector('[data-message-success]').classList.remove('hidden');
    } catch (err) {
      console.error(err);
      this.querySelector('[data-message-error]').classList.remove('hidden');
    } finally {
      this.#setLoadingState(false);
    }
  }
}

customElements.define('klaviyo-email-preferences', KlaviyoEmailPreferences);
